import { z } from 'zod';

// Platform identifier for Stripe metadata filtering
export const PLATFORM_NAME = 'wafer_inventory' as const;

export enum CONTAINER_TYPE {FOUP = 'FOUP', FOSB = 'FOSB', ClamShell = 'ClamShell'};
export enum CONTAINER_SIZE {Size150mm = '150mm', Size200mm = '200mm', Size300mm = '300mm', Size450mm = '450mm'}
export enum CONTAINER_STATUS {Available = 'Available', Shipped = 'Shipped', Lost = 'Lost'}
export enum WAFER_STATUS {
  Available = 'Available',
  Shipped = 'Shipped',
  Lost = 'Lost',
  /** In inventory but container/slot unknown (audit missing, reassign displacement, etc.) */
  Unlocated = 'Unlocated',
}
export enum WAFER_PATTERN_TYPE {Patterned = 'Patterned', Unpatterned = 'Unpatterned'}
export enum WAFER_MATERIAL_TYPE {Silicon = 'Silicon', Quartz = 'Quartz', GaN = 'GaN', Others = 'Others'}
export enum WAFER_THICKNESS_TYPE {Standard = 'Standard', Thick = 'Thick', Thin = 'Thin'}
export enum WAFER_SIZE {Size150mm = '150mm', Size200mm = '200mm', Size300mm = '300mm', Size450mm = '450mm'}
export enum EFEM_LOAD_PORT_SIZE {Size200mm = '200mm', Size300mm = '300mm', Size450mm = '450mm'}
export enum ALL_USER_ROLES {provider_admin = 'provider_admin', provider_engineer = 'provider_engineer', provider_support = 'provider_support', provider_deployment_engineer = 'provider_deployment_engineer', tenant_admin = 'tenant_admin',
tenant_lead = 'tenant_lead', tenant_engineer = 'tenant_engineer', unknown = 'unknown'}
export enum USER_TYPE {tenant = 'tenant', provider = 'provider'}
export enum BULK_OPERATION_TYPE {import = 'import', export = 'export', delete = 'delete', backup = 'backup', restore = 'restore'}
export enum SUBSCRIPTION_STATUS {Active = 'active', Trialing = 'trialing', Paused = 'paused', PastDue = 'past_due', Canceled = 'canceled', Incomplete = 'incomplete', Expired = 'expired'}
/** How subscription billing is collected — Stripe self-serve vs manual enterprise (Zoho/Mercury). */
export enum BILLING_MODE {Stripe = 'stripe', EnterpriseManual = 'enterprise_manual'}
/** Enterprise package kind — pilot evaluation vs annual contract package. */
export enum ENTERPRISE_PACKAGE_KIND {Pilot = 'pilot', Annual = 'annual', Amendment = 'amendment'}
export enum BILLING_CYCLE {Monthly = 'monthly', Yearly = 'yearly'}
export enum BILLING_RESOURCE_TYPE {Base = 'base', Wafers = 'wafers', Containers = 'containers', Users = 'users', AxonGateways = 'axon_gateways', Storage = 'storage', Encryption = 'encryption'}

// Wafer audit status types:
// OK - Wafer present in expected slot with correct fingerprint
// Mismatch - Wafer present in expected slot but fingerprint does not match database
// GhostWafer - Physical wafer found but no corresponding database record (extra wafer)
// MissingWafer - Database record exists but physical wafer not found in expected slot
// NotAudited - Wafer has not been audited yet
export enum WAFER_AUDIT_STATUS {OK = 'OK', Mismatch = 'Mismatch', GhostWafer = 'GhostWafer', MissingWafer = 'MissingWafer', NotAudited = 'NotAudited'}

// Wafer conflict management enums
export enum WAFER_CONFLICT_TYPE {
  GhostWafer = 'GhostWafer',        // Physical wafer found but no database record
  MissingWafer = 'MissingWafer',    // Database record exists but wafer not found
  Mismatch = 'Mismatch',            // Wafer in slot but fingerprint doesn't match
  Lost = 'Lost',                    // Wafer marked as lost in system
  Duplicate = 'Duplicate',          // Duplicate fingerprint or ID detected
  TransferFailure = 'TransferFailure', // EFEM transfer operation failed
}

export enum CONFLICT_STATUS {
  Open = 'Open',                    // Newly detected, not yet reviewed
  UnderReview = 'UnderReview',      // Being investigated
  Resolved = 'Resolved',            // Conflict resolved with action taken
  Dismissed = 'Dismissed',          // Acknowledged but no action needed
  Escalated = 'Escalated',          // Requires higher-level intervention
  Superseded = 'Superseded',        // Replaced by a newer audit-sourced conflict for same slot
}

export enum CONFLICT_SEVERITY {
  Critical = 'Critical',            // Immediate attention required
  High = 'High',                    // Important but not urgent
  Medium = 'Medium',                // Normal priority (default)
  Low = 'Low',                      // Minor issue
}

export enum RESOLUTION_ACTION {
  CorrectDatabase = 'CorrectDatabase',              // Legacy; use UpdateFingerprintFromScan for audit mismatch
  CorrectPhysical = 'CorrectPhysical',              // Legacy EFEM / non-audit
  MarkAsLost = 'MarkAsLost',
  MarkAsDispatched = 'MarkAsDispatched',            // MissingWafer → Shipped
  MarkAsUnlocated = 'MarkAsUnlocated',              // MissingWafer → Unlocated
  CreateNewRecord = 'CreateNewRecord',
  LinkExistingWafer = 'LinkExistingWafer',            // GhostWafer → existing wafer (incl. Unlocated)
  UpdateFingerprintFromScan = 'UpdateFingerprintFromScan', // Mismatch same wafer
  DismissInvalidScan = 'DismissInvalidScan',        // Mismatch — scan not trusted
  ReassignSlotOccupancy = 'ReassignSlotOccupancy',  // Mismatch different wafer
  ReconcileSlotSwap = 'ReconcileSlotSwap',          // Joint: two mismatches swapped
  ReconcileOccupancy = 'ReconcileOccupancy',        // Joint: missing + ghost
  RemoveFromDatabase = 'RemoveFromDatabase',
  MoveWafer = 'MoveWafer',                          // Legacy — not audit mismatch v1
  ReplaceWafer = 'ReplaceWafer',                    // Legacy
  RetryTransfer = 'RetryTransfer',
  NoActionRequired = 'NoActionRequired',
}

/** Persisted on audit_run.run_status */
export enum AUDIT_RUN_STATUS {
  pass = 'pass',
  needs_action = 'needs_action',
  resolved = 'resolved',
}

// OCR conflict resolution methods
export enum OCR_RESOLUTION_METHOD {
  StrongConsensus = 'strong_consensus',             // 2+ images agree exactly
  SimilarityAnalysis = 'similarity_analysis',       // Character-level similarity matching
  CharacterVoting = 'character_voting',             // Per-character voting across images
  QualityWeighted = 'quality_weighted',             // Quality/confidence weighted selection
  SingleImage = 'single_image',                     // Only one image available
  AllRejected = 'all_rejected',                     // All images below quality threshold
  ManualEntry = 'manual_entry',                     // User manually entered value
  UserOverride = 'user_override',                   // User manually overrode the resolved value
  Error = 'error',                                  // Error during resolution
}

export enum BACON_STATUS { Active = 'Active', Inactive = 'Inactive', LowBattery = 'LowBattery', Malfunction = 'Malfunction', Maintenance = 'Maintenance' }
export enum CONTAINER_HISTORY_EVENT { CREATED = 'CREATED', UPDATED = 'UPDATED', STATUS_CHANGED = 'STATUS_CHANGED', WAFER_ADDED = 'WAFER_ADDED', WAFER_REMOVED = 'WAFER_REMOVED',
BEACON_ASSIGNED = 'BEACON_ASSIGNED', BEACON_REMOVED = 'BEACON_REMOVED', LOCATION_CHANGED = 'LOCATION_CHANGED' }
export enum WAFER_HISTORY_EVENT { CREATED = 'CREATED', UPDATED = 'UPDATED', STATUS_CHANGED = 'STATUS_CHANGED', TRANSFERRED = 'TRANSFERRED', SHIPPED = 'SHIPPED', LOST = 'LOST', FINGERPRINT_UPDATED = 'FINGERPRINT_UPDATED', RETURNED = 'RETURNED' }

/** How a wafer container/slot change was initiated (stored on wafer_transfer_history). */
export enum WAFER_TRANSFER_SOURCE {
  ManualDetailPanel = 'manual_detail_panel',
  ManualTransferApi = 'manual_transfer_api',
  EfemBatchTransfer = 'efem_batch_transfer',
  SystemCreate = 'system_create',
  ConflictResolution = 'conflict_resolution',
  Legacy = 'legacy',
}

export function formatWaferTransferSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case WAFER_TRANSFER_SOURCE.ManualDetailPanel:
      return 'Manual (wafer detail)';
    case WAFER_TRANSFER_SOURCE.ManualTransferApi:
      return 'Manual (transfer tool)';
    case WAFER_TRANSFER_SOURCE.EfemBatchTransfer:
      return 'EFEM batch transfer';
    case WAFER_TRANSFER_SOURCE.SystemCreate:
      return 'Wafer created';
    case WAFER_TRANSFER_SOURCE.ConflictResolution:
      return 'Conflict resolution';
    case WAFER_TRANSFER_SOURCE.Legacy:
      return 'Legacy / unknown';
    default:
      return source?.trim() ? source : 'Unknown';
  }
}
export enum ONLINE_OFFLINE_STATUS { ONLINE = 'online', OFFLINE = 'offline'}
export enum LOAD_PORT_STATUS { EMPTY = 'EMPTY', DETECTED = 'DETECTED', LOADED = 'LOADED', ERROR = 'ERROR'}

/** Cloud load-port ↔ container binding resolution (efem_load_port_bindings). */
export enum EFEM_LOAD_PORT_BINDING_STATUS {
  MATCHED = 'matched',
  NOT_FOUND = 'not_found',
  /** Valid CNP- label not yet linked in inventory_labels (associate in UI first). */
  UNASSIGNED_LABEL = 'unassigned_label',
  /** Label linked to a non-container entity (e.g. future cage). */
  NOT_SUPPORTED = 'not_supported',
  PARSE_ERROR = 'parse_error',
  AMBIGUOUS = 'ambiguous',
  CONFLICT = 'conflict',
  CLEARED = 'cleared',
}

export enum EFEM_LOAD_PORT_BINDING_SOURCE {
  PASSIVE = 'passive',
  COMMAND = 'command',
  MANUAL = 'manual',
  HANDHELD = 'handheld',
  CLEARED = 'cleared',
}

// EFEM Scan Status - Backend database status
export enum EFEM_SCAN_STATUS {
  Initiated = 'initiated',       // Scan started, waiting for results
  InProgress = 'in_progress',    // Processing slots
  Completed = 'completed',       // All slots processed successfully
  Partial = 'partial',           // Some slots failed
  Failed = 'failed',             // All slots failed
  Aborted = 'aborted'            // Scan canceled
}

// EFEM Slot Result Status - Per-slot outcome reported by gateway
export enum EFEM_SLOT_STATUS {
  Success = 'success',           // Slot processed successfully (mapped from 'completed')
  Failed = 'failed',             // Slot processing failed
  NoWafer = 'no_wafer',          // No wafer detected in slot
  Uploading = 'uploading'        // Intermediate state - image upload in progress
}

// EFEM Image Capture Status - Adapter response status
export enum EFEM_CAPTURE_STATUS {
  Captured = 'captured',         // Image captured successfully
  NoWafer = 'no_wafer',          // No wafer detected
  Failed = 'failed'              // Capture failed
}

// EFEM Upload Status - S3 upload result status
export enum EFEM_UPLOAD_STATUS {
  Success = 'success',           // Upload successful
  Failed = 'failed'              // Upload failed
}

// EFEM Command Callback Status - Async command tracking
export enum EFEM_CALLBACK_STATUS {
  Pending = 'pending',           // Waiting for response
  Success = 'success',           // Command succeeded
  Failed = 'failed'              // Command failed
}

// EFEM IoT Commands
export enum EFEM_IOT_COMMAND {
  GET_STATUS = 'get_status',
  GET_LOADPORTS = 'get_loadports',
  GET_SLOTMAP = 'get_slotmap',
  LOAD_CONTAINERS = 'load_containers',
  UNLOAD_CONTAINERS = 'unload_containers',
  CAPTURE_WAFER_IMAGES = 'capture_wafer_images',
  CAPTURE_WAFER_IMAGES_AT_ANGLES = 'capture_wafer_images_at_angles',
  INSPECT_CONTAINER_SLOT = 'inspect_container_slot',
  INSPECT_CONTAINER_SLOTS = 'inspect_container_slots',
  SCAN_CONTAINER = 'scan_container',
  TRANSFER_WAFER = 'transfer_wafer',
  EXECUTE_BATCH_WAFER_TRANSFER = 'execute_batch_wafer_transfer',
  ABORT_BATCH_WAFER_TRANSFER = 'abort_batch_wafer_transfer',
  ABORT_SCAN = 'abort_scan',
  PROBE_PERIPHERALS = 'probe_peripherals',
}

/** Per-job lighting resolution for batch scan / recapture (REST only — not on MQTT). */
export enum LIGHTING_SOURCE {
  TEMPLATE_STARTING = 'template_starting',
  WAFER_LEARNED = 'wafer_learned',
}

/** Unified jobs API job family discriminator */
export enum UNIFIED_JOB_TYPE {
  CONTAINER_SLOT_SCAN = 'container_slot_scan',
  BATCH_WAFER_TRANSFER = 'batch_wafer_transfer',
}

export enum JOB_SUCCESS_TIER {
  FULLY_SUCCESSFUL = 'fully_successful',
  PARTIALLY_SUCCESSFUL = 'partially_successful',
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress',
}

export enum ENTITY_NOTE_ENTITY_TYPE {
  CONTAINER = 'container',
  WAFER = 'wafer',
  WAFER_CONFLICT = 'wafer_conflict',
}

export enum CONTAINER_WORKFLOW_TYPE {
  CONTAINER_CREATE = 'container_create',
  WAFER_DATA_REFRESH = 'wafer_data_refresh',
  CONTAINER_AUDIT = 'container_audit',
}

export enum CONTAINER_WORKFLOW_DRAFT_STATUS {
  DRAFT = 'draft',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

// Batch Scan Orchestrator - Scan status
export enum BATCH_SCAN_STATUS {
  INITIATED = 'initiated',
  IN_PROGRESS = 'in_progress',
  OCR_PROCESSING = 'ocr_processing',
  COMPLETED = 'completed',
  PARTIAL = 'partial',        // Some slots succeeded, some failed
  FAILED = 'failed',
  ABORTED = 'aborted',
}

// Batch Scan Orchestrator - Per-slot status
export enum SLOT_SCAN_STATUS {
  /** Slot not started yet (manifest row only). */
  PENDING = 'pending',
  /** Edge is picking/capturing/returning; no terminal outcome yet. */
  IN_PROGRESS = 'in_progress',
  /** Images registered; OCR queue/Lambda still running. */
  OCR_PROCESSING = 'ocr_processing',
  SUCCESS = 'success',
  NO_WAFER = 'no_wafer',
  SCAN_FAILED = 'scan_failed',
  OCR_FAILED = 'ocr_failed',
  ABORTED = 'aborted',
}

/** Container slot scan job state machine (replaces BATCH_SCAN_STATUS on new tables). */
export enum CONTAINER_SLOT_SCAN_JOB_PHASE {
  CREATED = 'created',
  COMMAND_PUBLISHED = 'command_published',
  CONTAINER_SLOT_SCANNING = 'container_slot_scanning',
  OCR = 'ocr',
  WORKFLOW = 'workflow',
  COMPLETED = 'completed',
  PARTIAL = 'partial',
  FAILED = 'failed',
  ABORTED = 'aborted',
}

/** Live slot progress mirrored from edge telemetry. */
export enum CONTAINER_SLOT_SCAN_SLOT_PHASE {
  PENDING = 'pending',
  PICKING = 'picking',
  ALIGNING = 'aligning',
  CAPTURING = 'capturing',
  RETURNING = 'returning',
  DONE = 'done',
}

export enum CONTAINER_SLOT_SCAN_SLOT_OUTCOME {
  SUCCESS = 'success',
  FAILED = 'failed',
  NO_WAFER = 'no_wafer',
  ABORTED = 'aborted',
}

export enum CONTAINER_SLOT_SCAN_OCR_PHASE {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

/** Batch wafer transfer job (EFEM robot moves). */
export enum BATCH_WAFER_TRANSFER_JOB_PHASE {
  CREATED = 'created',
  COMMAND_PUBLISHED = 'command_published',
  TRANSFERRING = 'transferring',
  COMPLETING = 'completing',
  COMPLETED = 'completed',
  PARTIAL = 'partial',
  FAILED = 'failed',
  ABORTED = 'aborted',
}

export enum BATCH_WAFER_TRANSFER_ITEM_PHASE {
  PENDING = 'pending',
  PICKING = 'picking',
  MOVING = 'moving',
  PLACING = 'placing',
  DONE = 'done',
}

export enum BATCH_WAFER_TRANSFER_ITEM_OUTCOME {
  SUCCESS = 'success',
  FAILED = 'failed',
  ABORTED = 'aborted',
}

export enum FINGERPRINT_INFERENCE_BATCH_PHASE {
  CREATED = 'created',
  INFERRING = 'inferring',
  RESOLVED = 'resolved',
  FAILED = 'failed',
}

export enum FINGERPRINT_INFERENCE_BATCH_CONTEXT {
  MANUAL_DIAGNOSTIC = 'manual_diagnostic',
  WAFER_REVIEW = 'wafer_review',
  ML_VALIDATION = 'ml_validation',
  RECAPTURE_PREVIEW = 'recapture_preview',
  /** Manual wafer-ID grab at prealigner (no EFEM motion from Cnapse). */
  STATION_ACQUIRE = 'station_acquire',
}

// Batch Scan Orchestrator - OCR error classification
export enum OCR_ERROR_TYPE {
  TRANSIENT = 'transient',    // Network, timeout, 500 errors - retry
  PERMANENT = 'permanent',    // Invalid format, file not found - fail
  QUALITY = 'quality',        // Image too blurry - fail but allow manual retry
}

// OCR_CONTEXT
export enum OCR_CONTEXT_TYPE {
  EFEM_SCAN = 'efem_scan',   
  MANUAL_UPLOAD = 'manual_upload',   
  AUDIT = 'audit',       
}

// IoT Device Status
export enum IOT_STATUS {
  PROVISIONED = 'provisioned',
  ACTIVE = 'active',
  REVOKED = 'revoked'
}

// Health Check Status
export enum HEALTH_STATUS {
  OK = 'ok',
  DEGRADED = 'degraded',
  ERROR = 'error'
}

export enum HEALTH_CHECK_STATUS {
  OK = 'ok',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
  UNKNOWN = 'unknown'
}

export enum READINESS_STATUS {
  READY = 'ready',
  NOT_READY = 'not ready'
}

export enum LIVENESS_STATUS {
  ALIVE = 'alive'
}

export enum ML_COMPONENT_STATUS {
  READY = 'ready',
  NOT_READY = 'notReady'
}

export enum IMAGE_QUALITY {
  EXCELLENT = 'excellent',
  GOOD = 'good',
  FAIR = 'fair',
  POOR = 'poor'
}

// File entity types (polymorphic file storage)
export enum FILE_ENTITY_TYPE {
  // Tenant-scoped entities
  Wafer = 'wafer',
  Container = 'container',
  WaferConflict = 'wafer_conflict',
  ContainerAudit = 'container_audit',
  TempFingerprint = 'temp_fingerprint',
  TempFile = 'temp_file',
  TenantReport = 'tenant_report',
  //TenantInvoice = 'tenant_invoice',
  TenantExport = 'tenant_export',
  AiModel = 'ai_model',
  FloorPlan = 'floor_plan',
  EntityNote = 'entity_note',
  // Provider-scoped entities (no tenant)
  ProviderReport = 'provider_report',
  ProviderInvoice = 'provider_invoice',
  SystemTemplate = 'system_template',
  SystemBackup = 'system_backup',
  SystemConfig = 'system_config'
}

// File categories for organization and filtering
export enum FILE_CATEGORY {
  Image = 'image',
  Document = 'document',
  FingerprintImage = 'fingerprint_image',
  TempFingerprint = 'temp_fingerprint',
  TempFile = 'temp_file',
  ReportPdf = 'report_pdf',
  ReportCsv = 'report_csv',
  ReportXlsx = 'report_xlsx',
  ModelWeights = 'model_weights',
  TrainingData = 'training_data',
  InvoicePdf = 'invoice_pdf',
  ExportArchive = 'export_archive'
}

// Helper function to map orchestrator 'completed' status to backend 'success'
export function mapSlotStatusForBackend(orchestratorStatus: string): EFEM_SLOT_STATUS {
  if (orchestratorStatus === 'completed') return EFEM_SLOT_STATUS.Success;
  if (orchestratorStatus === 'failed') return EFEM_SLOT_STATUS.Failed;
  if (orchestratorStatus === 'no_wafer') return EFEM_SLOT_STATUS.NoWafer;
  if (orchestratorStatus === 'uploading') return EFEM_SLOT_STATUS.Uploading;
  return EFEM_SLOT_STATUS.Failed; // Default to failed for unknown statuses
}

// Helper function to check if a status is a final status (not intermediate like 'uploading')
export function isFinalSlotStatus(orchestratorStatus: string): boolean {
  const mapped = mapSlotStatusForBackend(orchestratorStatus);
  return mapped !== EFEM_SLOT_STATUS.Uploading;
}

// Location device types
export enum LOCATION_DEVICE_TYPE {
  Anchor = 'anchor',
  Tag = 'tag',
}


/** States that are allowed to write into tenant-visible current/history/event stores. */
/*export const LOCATION_DEVICE_ALLOWED_STATES: ReadonlySet<DeviceInventoryStatus> = new Set([
  DeviceInventoryStatusSchema.Enum.claimed,
  DeviceInventoryStatusSchema.Enum.active,
]);*/

export const allUserRoleSchema = z.nativeEnum(ALL_USER_ROLES);

export function getRoleDisplayName(role: ALL_USER_ROLES ): string {

    if (role === ALL_USER_ROLES.tenant_admin || role === ALL_USER_ROLES.provider_admin) return 'Administrator';
    if (role === ALL_USER_ROLES.tenant_engineer || role === ALL_USER_ROLES.provider_engineer) return 'Engineer';
    if (role === ALL_USER_ROLES.tenant_lead ) return 'Lead';
    if (role === ALL_USER_ROLES.provider_support) return 'Support';
    if (role === ALL_USER_ROLES.provider_deployment_engineer) return 'Deployment Engineer';
    return role.toString();
}

export function isProviderRole(role: ALL_USER_ROLES | string | undefined | null): boolean {
    if (!role) return false;

    return role === ALL_USER_ROLES.provider_admin ||
           role === ALL_USER_ROLES.provider_engineer ||
           role === ALL_USER_ROLES.provider_support ||
           role === ALL_USER_ROLES.provider_deployment_engineer;
}

export function isTenantRole(role: ALL_USER_ROLES | string | undefined | null): boolean {
    if (!role) return false;

    return role === ALL_USER_ROLES.tenant_admin ||
           role === ALL_USER_ROLES.tenant_lead ||
           role === ALL_USER_ROLES.tenant_engineer;
}

export function sizeMatch(waferSize: WAFER_SIZE, containerSize: CONTAINER_SIZE): boolean {
    if(waferSize === WAFER_SIZE.Size150mm && containerSize === CONTAINER_SIZE.Size150mm) return true;
    if(waferSize === WAFER_SIZE.Size200mm && containerSize === CONTAINER_SIZE.Size200mm) return true;
    if(waferSize === WAFER_SIZE.Size300mm && containerSize === CONTAINER_SIZE.Size300mm) return true;
    if(waferSize === WAFER_SIZE.Size450mm && containerSize === CONTAINER_SIZE.Size450mm) return true;
    return false;
}

/** Map EFEM load port size to inventory container size (same mm label where supported). */
export function loadPortSizeToContainerSize(
    loadPortSize: EFEM_LOAD_PORT_SIZE | string | null | undefined,
): CONTAINER_SIZE {
    switch (loadPortSize) {
        case EFEM_LOAD_PORT_SIZE.Size200mm:
            return CONTAINER_SIZE.Size200mm;
        case EFEM_LOAD_PORT_SIZE.Size450mm:
            return CONTAINER_SIZE.Size450mm;
        case EFEM_LOAD_PORT_SIZE.Size300mm:
        default:
            return CONTAINER_SIZE.Size300mm;
    }
}

import {
    type FirstName,
    type LastName,
    type CompanyName,
    type CompanyAlias,
    type Email,
    type Uuid,
    type IntegerId,
    type PhoneNumber,
    type Password,
    type NewPassword,
    type Date,
    type URL,
    type WaferFingerprint
} from './schemas.js'


export type {
    FirstName,
    LastName,
    CompanyName,
    CompanyAlias,
    Email,
    Uuid,
    IntegerId,
    PhoneNumber,
    Password,
    NewPassword,
    Date,
    URL,
    WaferFingerprint
}